import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import AboutModal from './AboutModal';
import PageTransition from './PageTransition';
import { useRouteTransition } from '../hooks/useRouteTransition';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [showAbout, setShowAbout] = useState(false);
  const [time, setTime] = useState(new Date());
  const { isTransitioning } = useRouteTransition();

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen bg-black text-white font-rajdhani relative overflow-hidden">
      {/* Scan Line Effect */}
      <div className="scan-line" />
      
      {/* Enhanced Background Portrait with Glitch Effects */}
      <div className="fixed inset-0 z-[1]">
        {/* Primary glitched portrait layer */}
        <motion.div 
          className="absolute right-8 top-0 h-full w-auto bg-cover bg-center bg-no-repeat opacity-30"
          style={{
            backgroundImage: `url('/bashexo2.png')`,
            backgroundSize: 'auto 100%',
            backgroundPosition: 'right center',
            backgroundRepeat: 'no-repeat',
            aspectRatio: '3/4',
            filter: 'contrast(1.1) brightness(1.05) saturate(0.9)',
            maskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.3) 20%, rgba(0,0,0,0.7) 40%, rgba(0,0,0,0.9) 60%, rgba(0,0,0,1) 80%)',
            WebkitMaskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.3) 20%, rgba(0,0,0,0.7) 40%, rgba(0,0,0,0.9) 60%, rgba(0,0,0,1) 80%)'
          }}
          animate={{
            filter: [
              'contrast(1.1) brightness(1.05) saturate(0.9)',
              'contrast(1.2) brightness(0.95) saturate(0.85) hue-rotate(2deg)',
              'contrast(1.1) brightness(1.05) saturate(0.9)',
              'contrast(1.15) brightness(1.0) saturate(0.88) hue-rotate(-1deg)',
              'contrast(1.1) brightness(1.05) saturate(0.9)'
            ]
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
        />

        {/* Glitch displacement layers */}
        <motion.div 
          className="absolute right-8 top-0 h-full w-auto bg-cover bg-center bg-no-repeat opacity-10"
          style={{
            backgroundImage: `url('/bashexo2.png')`,
            backgroundSize: 'auto 100%',
            backgroundPosition: 'right center',
            backgroundRepeat: 'no-repeat',
            aspectRatio: '3/4',
            filter: 'hue-rotate(15deg) saturate(1.1) brightness(1.1) contrast(0.9)',
            mixBlendMode: 'soft-light',
            maskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.3) 20%, rgba(0,0,0,0.7) 40%, rgba(0,0,0,0.9) 60%, rgba(0,0,0,1) 80%)',
            WebkitMaskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.3) 20%, rgba(0,0,0,0.7) 40%, rgba(0,0,0,0.9) 60%, rgba(0,0,0,1) 80%)'
          }}
          animate={{
            x: [0, 1, -0.5, 1.5, 0],
            y: [0, -0.5, 1, -1, 0],
            opacity: [0.1, 0.15, 0.08, 0.12, 0.1]
          }}
          transition={{
            duration: 1.2,
            repeat: Infinity,
            ease: 'easeInOut',
            repeatDelay: 4
          }}
        />

        <motion.div 
          className="absolute right-8 top-0 h-full w-auto bg-cover bg-center bg-no-repeat opacity-8"
          style={{
            backgroundImage: `url('/bashexo2.png')`,
            backgroundSize: 'auto 100%',
            backgroundPosition: 'right center',
            backgroundRepeat: 'no-repeat',
            aspectRatio: '3/4',
            filter: 'hue-rotate(-10deg) saturate(1.2) brightness(1.2) contrast(1.1)',
            mixBlendMode: 'overlay',
            maskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.3) 20%, rgba(0,0,0,0.7) 40%, rgba(0,0,0,0.9) 60%, rgba(0,0,0,1) 80%)',
            WebkitMaskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.3) 20%, rgba(0,0,0,0.7) 40%, rgba(0,0,0,0.9) 60%, rgba(0,0,0,1) 80%)'
          }}
          animate={{
            x: [0, -1.5, 0.5, -1, 0],
            y: [0, 1, -0.5, 1.5, 0],
            opacity: [0.08, 0.0, 0.12, 0.04, 0.08]
          }}
          transition={{
            duration: 1.0,
            repeat: Infinity,
            ease: 'linear',
            repeatDelay: 3.5
          }}
        />

        {/* RGB split effect */}
        <motion.div 
          className="absolute right-8 top-0 h-full w-auto bg-cover bg-center bg-no-repeat opacity-6"
          style={{
            backgroundImage: `url('/bashexo2.png')`,
            backgroundSize: 'auto 100%',
            backgroundPosition: 'right center',
            backgroundRepeat: 'no-repeat',
            aspectRatio: '3/4',
            filter: 'sepia(0.3) hue-rotate(5deg) saturate(1.3) brightness(1.1) contrast(0.95)',
            maskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.3) 20%, rgba(0,0,0,0.7) 40%, rgba(0,0,0,0.9) 60%, rgba(0,0,0,1) 80%)',
            WebkitMaskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.3) 20%, rgba(0,0,0,0.7) 40%, rgba(0,0,0,0.9) 60%, rgba(0,0,0,1) 80%)'
          }}
          animate={{
            x: [0, 0.5, -1, 0.5, 0],
            opacity: [0.06, 0.0, 0.09, 0.0, 0.06]
          }}
          transition={{
            duration: 0.5,
            repeat: Infinity,
            ease: 'linear',
            repeatDelay: 6
          }}
        />

        {/* Scanline overlay on portrait */}
        <motion.div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,255,247,0.03) 2px, rgba(0,255,247,0.03) 4px)',
            backgroundSize: '100% 4px'
          }}
          animate={{
            backgroundPositionY: ['0px', '4px']
          }}
          transition={{
            duration: 0.1,
            repeat: Infinity,
            ease: 'linear'
          }}
        />

        {/* Digital noise overlay */}
        <motion.div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `radial-gradient(circle, rgba(0,255,247,0.1) 1px, transparent 1px)`,
            backgroundSize: '20px 20px'
          }}
          animate={{
            backgroundPosition: ['0px 0px', '20px 20px', '0px 40px', '20px 0px', '0px 0px']
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'linear'
          }}
        />

        {/* Subtle gradient transition from left dark to reveal photo */}
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 via-black/60 via-black/35 via-black/15 to-transparent opacity-90" />
      </div>

      {/* Grid Pattern */}
      <div className="fixed inset-0 opacity-10 z-[2]">
        <div 
          className="absolute inset-0 bg-cyber-grid" 
          style={{ backgroundSize: '80px 80px' }}
        />
      </div>

      {/* Floating Particles */}
      <div className="fixed inset-0 pointer-events-none z-[3]">
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-cyber-teal rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -100, 0],
              opacity: [0, 1, 0],
              scale: [0, 1, 0],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      {/* Top HUD Bar */}
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-md border-b border-white/20"
        style={{ backgroundColor: 'rgba(0,0,0,0.98)' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3 sm:space-x-6">
              {/* BR Logo */}
              <motion.div 
                className="flex items-center space-x-2 sm:space-x-3"
                whileHover={{ scale: 1.05 }}
              >
                <img 
                  src="/br-logo.png" 
                  alt="BR Logo" 
                  className="w-6 h-6 sm:w-8 sm:h-8 opacity-60" 
                />
                <div className="glitch-text font-orbitron font-bold text-lg sm:text-xl cyber-glow-strong" data-text="B.a.s.H">
                  B.a.s.H
                </div>
              </motion.div>
              
              <div className="hidden md:flex items-center space-x-4 text-xs font-mono">
                <img src="/br-logo.png" alt="BR" className="w-4 h-4 opacity-40" />
                <div className="text-gray-500">NEURAL_CORE.EXE</div>
                <img src="/br-logo.png" alt="BR" className="w-4 h-4 opacity-40" />
                <div className="text-gray-400">v3.0.1</div>
              </div>
            </div>
            
            <div className="flex items-center space-x-3 sm:space-x-6">
              <div className="hidden sm:flex items-center space-x-2 sm:space-x-6 text-xs font-mono">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse cyber-glow"></div>
                  <span className="text-green-400 cyber-glow hidden md:inline">ONLINE</span>
                </div>
                <div className="text-white/60 hidden lg:block">
                  {time.toISOString().split('T')[0]}
                </div>
                <div className="text-white/60 hidden md:block">
                  {time.toTimeString().split(' ')[0]}
                </div>
              </div>
              
              <motion.button
                onClick={() => setShowAbout(true)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-2 sm:px-4 py-1 sm:py-2 border border-white/30 hover:border-white/60 hover:bg-white/5 transition-all duration-300 text-xs font-orbitron uppercase tracking-wide text-white/80 hover:text-white cyber-glow"
              >
                <span className="hidden sm:inline">[IDENTITY]</span>
                <span className="sm:hidden">[ID]</span>
              </motion.button>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <main className="pt-16 pb-16 relative z-[10]">
        <PageTransition isLoading={isTransitioning}>
          {children}
        </PageTransition>
      </main>

      {/* Bottom HUD Bar */}
      <motion.footer
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        className="fixed bottom-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-md border-t border-white/20"
        style={{ backgroundColor: 'rgba(0,0,0,0.98)' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3 sm:space-x-8 text-xs font-mono">
              <div className="flex items-center space-x-2">
                <img src="/br-logo.png" alt="BR" className="w-3 h-3 opacity-30 hidden sm:block" />
                <div className="w-1 h-1 bg-yellow-400 animate-pulse"></div>
                <span className="text-gray-500 hidden sm:inline">UTC: {time.toISOString().split('T')[1].split('.')[0]}</span>
                <span className="text-gray-500 sm:hidden">{time.toTimeString().split(' ')[0]}</span>
              </div>
              <div className="flex items-center space-x-2 hidden md:flex">
                <div className="w-1 h-1 bg-blue-400 animate-pulse"></div>
                <span className="text-gray-500">UPTIME: ∞</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-1 h-1 bg-green-400 animate-pulse"></div>
                <span className="text-gray-500 hidden sm:inline">STATUS: ACTIVE</span>
                <span className="text-gray-500 sm:hidden">ACTIVE</span>
                <img src="/br-logo.png" alt="BR" className="w-3 h-3 opacity-30 hidden sm:block" />
              </div>
            </div>
            
            <div className="flex items-center space-x-3 sm:space-x-6">
              <motion.a
                href="https://github.com"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1 }}
                className="text-gray-500 hover:text-white transition-all duration-300 text-xs font-mono cyber-glow"
              >
                <span className="hidden sm:inline">[GITHUB]</span>
                <span className="sm:hidden">[GIT]</span>
              </motion.a>
              <motion.a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1 }}
                className="text-gray-500 hover:text-white transition-all duration-300 text-xs font-mono cyber-glow"
              >
                <span className="hidden sm:inline">[LINKEDIN]</span>
                <span className="sm:hidden">[LI]</span>
              </motion.a>
              <motion.a
                href="mailto:muhamadbashirushdan@gmail.com"
                whileHover={{ scale: 1.1 }}
                className="text-gray-500 hover:text-white transition-all duration-300 text-xs font-mono cyber-glow"
              >
                <span className="hidden sm:inline">[CONTACT]</span>
                <span className="sm:hidden">[@]</span>
              </motion.a>
            </div>
          </div>
        </div>
      </motion.footer>

      {/* About Modal */}
      <AboutModal isOpen={showAbout} onClose={() => setShowAbout(false)} />
      
      {/* Advanced Glitch Effects */}
      <div className="fixed inset-0 pointer-events-none z-40">
        {/* Corner Decorations */}
        <div className="absolute top-4 left-4 w-8 h-8 border-l-2 border-t-2 border-cyber-teal/30"></div>
        <div className="absolute top-4 right-4 w-8 h-8 border-r-2 border-t-2 border-cyber-teal/30"></div>
        <div className="absolute bottom-4 left-4 w-8 h-8 border-l-2 border-b-2 border-cyber-teal/30"></div>
        <div className="absolute bottom-4 right-4 w-8 h-8 border-r-2 border-b-2 border-cyber-teal/30"></div>
        
        {/* Random Glitch Elements */}
        <motion.div 
          className="absolute top-1/3 left-1/4 w-2 h-8 bg-cyber-pink/30"
          animate={{ opacity: [0, 1, 0] }}
          transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 3 }}
        />
        <motion.div 
          className="absolute top-2/3 right-1/3 w-4 h-1 bg-cyber-purple/40"
          animate={{ opacity: [0, 1, 0], x: [-10, 10, -10] }}
          transition={{ duration: 0.8, repeat: Infinity, repeatDelay: 2 }}
        />
        <motion.div 
          className="absolute bottom-1/3 left-1/2 w-1 h-6 bg-cyber-aqua/20"
          animate={{ opacity: [0, 1, 0], scaleY: [1, 2, 1] }}
          transition={{ duration: 1, repeat: Infinity, repeatDelay: 4 }}
        />
      </div>
      
      {/* Data Stream Effect */}
      <div className="fixed top-0 right-8 w-px h-full bg-gradient-to-b from-transparent via-cyber-teal/20 to-transparent pointer-events-none z-30">
        <motion.div
          className="w-full h-20 bg-cyber-teal/40"
          animate={{ y: ['-100%', '100vh'] }}
          transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
        />
      </div>
    </div>
  );
};

export default Layout;